import dayjs, { Dayjs } from 'dayjs';

import customParseFormat from 'dayjs/plugin/customParseFormat';

import type { Cell } from 'exceljs';

dayjs.extend(customParseFormat);

/**
 * Parse a boolean value flexibly from user input.
 * Accepts as true: "X", "true", "yes", "1", or the field name
 * (e.g., "Shotgun" for key "isShotgun", "Golf" for key "isGolf").
 * Everything else (including empty/undefined) is false.
 */
export function parseBooleanFlexible(value: string | undefined, key?: string): boolean {
  if (!value) return false;
  const v = value.trim().toLowerCase();
  if (['x', 'true', 'yes', '1'].includes(v)) return true;
  // Match the field name itself (strip "is" prefix, e.g. "isShotgun" → "shotgun")
  if (key && key.startsWith('is')) {
    const fieldName = key.slice(2).toLowerCase();
    if (v === fieldName) return true;
  }
  return false;
}

/** Date formats to try in order (dayjs customParseFormat). */
const DATE_FORMATS = [
  'M/D/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD',
  'M-D-YYYY', 'MM-DD-YYYY',
  'M/D/YY', 'MM/DD/YY',
];

/**
 * Parse a date string flexibly, trying multiple common formats.
 * Returns a dayjs object (date only, no time) or null if unparseable.
 */
export function parseDateFlexible(value: string): Dayjs | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  for (const fmt of DATE_FORMATS) {
    const d = dayjs(trimmed, fmt, true);
    if (d.isValid()) return d;
  }

  // Fallback: let dayjs try natively
  const fallback = dayjs(trimmed);
  return fallback.isValid() ? fallback : null;
}

/** Time formats to try in order (dayjs customParseFormat). */
const TIME_FORMATS = [
  'h:mm A', 'h:mma', 'h:mmA',
  'h A', 'ha', 'hA',
  'HH:mm', 'H:mm',
  'h:mm',
];

/**
 * Parse a time string flexibly into hour/minute.
 * Returns { hour, minute } or null if unparseable.
 */
export function parseTimeFlexible(value: string): { hour: number; minute: number } | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  for (const fmt of TIME_FORMATS) {
    const t = dayjs(trimmed, fmt, true);
    if (t.isValid()) return { hour: t.hour(), minute: t.minute() };
  }
  return null;
}

/**
 * Parse a duration value flexibly into minutes.
 * Accepts:
 *   - "3 hr 30 min", "3 hours 30 minutes", "3h 30m" → 210
 *   - "3:30" → 210 (hours:minutes)
 *   - "3.5" → 210 (decimal hours)
 *   - "3" or "5" (≤ 24) → hours → 180 or 300
 *   - "300" (> 24) → minutes (backwards compatible)
 * Returns minutes as a number, or null if unparseable.
 */
export function parseDurationFlexible(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  // Pattern: "3 hr 30 min", "3 hours", "2h 15m", "3 hr", "30 min"
  const hrMinMatch = trimmed.match(/^(\d+)\s*(?:hr|hours?|h)\s*(?:(\d+)\s*(?:min|minutes?|m))?$/i);
  if (hrMinMatch) {
    const hours = parseInt(hrMinMatch[1]);
    const minutes = hrMinMatch[2] ? parseInt(hrMinMatch[2]) : 0;
    return hours * 60 + minutes;
  }

  // Pattern: minutes only "30 min", "45 minutes"
  const minOnlyMatch = trimmed.match(/^(\d+)\s*(?:min|minutes?|m)$/i);
  if (minOnlyMatch) {
    return parseInt(minOnlyMatch[1]);
  }

  // Pattern: "H:MM" (colon-separated hours:minutes)
  const colonMatch = trimmed.match(/^(\d+):(\d{1,2})$/);
  if (colonMatch) {
    const hours = parseInt(colonMatch[1]);
    const minutes = parseInt(colonMatch[2]);
    return hours * 60 + minutes;
  }

  // Decimal number → hours (e.g., "3.5" → 210)
  if (/^\d+\.\d+$/.test(trimmed)) {
    return Math.round(parseFloat(trimmed) * 60);
  }

  // Plain integer
  if (/^\d+$/.test(trimmed)) {
    const n = parseInt(trimmed);
    // ≤ 24 → treat as hours; > 24 → treat as minutes
    return n <= 24 ? n * 60 : n;
  }

  return null;
}

/**
 * Normalize an imported email address: trimmed and lowercased. A space is only legal inside a
 * quoted local part, so an address typed without the quotes ("test golfer850@mailinator.com")
 * is stored in its standard form ("\"test golfer850\"@mailinator.com"). Already-quoted addresses
 * are kept as they are.
 */
export function parseEmailFlexible(value: string | undefined): string {
  const email = (value ?? '').trim().toLowerCase();
  const at = email.lastIndexOf('@');
  if (at <= 0) return email;
  const local = email.slice(0, at);
  const isQuoted = local.length > 1 && local.startsWith('"') && local.endsWith('"');
  if (isQuoted || !/\s/.test(local)) return email;
  return `"${local.replace(/(["\\])/g, '\\$1')}"${email.slice(at)}`;
}

/** Parse a day's hole count: only full (18) and nine-hole (9) rounds are supported. */
export function parseNumHoles(value: string): 9 | 18 | null {
  const trimmed = value.trim();
  if (trimmed === '18') return 18;
  if (trimmed === '9') return 9;
  return null;
}

/** Parse which nine a nine-hole day plays: "Front" / "Back", also "Front 9", "back nine", etc. */
export function parseWhich9(value: string): 'front' | 'back' | null {
  const v = value.trim().toLowerCase();
  if (v.startsWith('front')) return 'front';
  if (v.startsWith('back')) return 'back';
  return null;
}

/** File types every CSV importer accepts — pass to a file input's `accept`. */
export const SPREADSHEET_FILE_ACCEPT = '.csv,.xlsx';

/**
 * Reads an uploaded .csv or .xlsx file as CSV text. An .xlsx file's first worksheet is
 * converted, so every importer runs both formats through the same CSV parsing.
 */
export async function readSpreadsheetFile(file: File): Promise<string> {
  return /\.xlsx$/i.test(file.name) ? xlsxToCsvText(await file.arrayBuffer()) : file.text();
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/**
 * Formats a spreadsheet date the way it would be typed into a CSV, so the flexible date/time
 * parsers above accept it. ExcelJS hands dates back in UTC; a time-only cell lands on Excel's
 * day zero (1899-12-30).
 */
function formatSpreadsheetDate(date: Date): string {
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const time = `${hours % 12 || 12}:${pad2(minutes)} ${hours < 12 ? 'AM' : 'PM'}`;
  if (date.getUTCFullYear() < 1900) return time;
  const day = `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${date.getUTCFullYear()}`;
  return hours === 0 && minutes === 0 ? day : `${day} ${time}`;
}

/** The text a cell shows: a formula's result, a rich text cell's plain text, errors as blank. */
function spreadsheetCellText(cell: Cell): string {
  const { value } = cell;
  const shown = value !== null && typeof value === 'object' && 'result' in value ? value.result : value;
  if (shown instanceof Date) return formatSpreadsheetDate(shown);
  if (shown !== null && typeof shown === 'object' && 'error' in shown) return '';
  return cell.text ?? '';
}

/**
 * Converts the first worksheet of an XLSX workbook (as an ArrayBuffer) to CSV text,
 * so it can be fed through the same CSV parsing pipeline used for a native .csv upload.
 */
export async function xlsxToCsvText(buffer: ArrayBuffer): Promise<string> {
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);
  const worksheet = workbook.worksheets[0];
  if (!worksheet) return '';

  const csvRows: string[] = [];
  worksheet.eachRow((row) => {
    const cells: string[] = [];
    row.eachCell({ includeEmpty: true }, (cell) => {
      const str = spreadsheetCellText(cell);
      cells.push(/[",\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str);
    });
    csvRows.push(cells.join(','));
  });
  return csvRows.join('\n');
}

/** Map of friendly team composition labels to their stored values. */
const TEAM_COMPOSITION_MAP: Record<string, string> = {
  'member only': 'MEMBER_ONLY',
  'guest only': 'GUEST_ONLY',
  'member/guest': 'MEMBER_GUEST',
  'open': 'MEMBER_GUEST',
};

/**
 * Parse a team composition value flexibly.
 * Accepts friendly text ("Member Only", "Member/Guest", "Open")
 * or the raw enum values ("MEMBER_ONLY", "GUEST_ONLY", "MEMBER_GUEST").
 * Returns the canonical value or the original string if unrecognized.
 */
export function parseTeamComposition(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;

  // Check friendly label (case-insensitive)
  const mapped = TEAM_COMPOSITION_MAP[trimmed.toLowerCase()];
  if (mapped) return mapped;

  // Already a valid raw value — return as-is
  return trimmed;
}
